import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { getAllGatheringEventsWithFlyer } from '~/api/gatherings';
import { IEventWithFlyer, IPaginationMeta } from '~/types/gathering';

const DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 2;

const QUERY_KEY = ['eventsWithFlyer', 'uncompleted'];

export default function useFetchEventsWithFlyer() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery<
    IEventWithFlyer[]
  >(
    QUERY_KEY,
    async () => {
      const res = await getAllGatheringEventsWithFlyer({ completed: false });
      return res.data;
    },
    {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    }
  );

  const allItems = data ?? [];

  const items = debouncedSearch
    ? allItems.filter((e) =>
        e.buildingName?.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : allItems;

  const total = items.length;

  const pagination: IPaginationMeta = {
    page: 1,
    limit: total,
    total,
    totalPages: total > 0 ? 1 : 0,
    hasMore: false,
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      const trimmed = value.trim();
      if (trimmed.length === 0 || trimmed.length >= MIN_SEARCH_LENGTH) {
        setDebouncedSearch(trimmed);
      }
    }, DEBOUNCE_MS);
  }, []);

  const clearSearch = useCallback(() => {
    setSearch('');
    setDebouncedSearch('');
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
  }, []);

  const updateItemInCache = useCallback(
    (eventUuid: string, updater: (item: IEventWithFlyer) => IEventWithFlyer) => {
      queryClient.setQueryData<IEventWithFlyer[] | undefined>(
        QUERY_KEY,
        (old) => {
          if (!old) return old;
          return old.map((item) =>
            item.uuid === eventUuid ? updater(item) : item
          );
        }
      );
    },
    [queryClient]
  );

  const removeItemFromCache = useCallback(
    (eventUuid: string) => {
      queryClient.setQueryData<IEventWithFlyer[] | undefined>(
        QUERY_KEY,
        (old) => {
          if (!old) return old;
          return old.filter((item) => item.uuid !== eventUuid);
        }
      );
    },
    [queryClient]
  );

  return {
    items,
    pagination,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    completed: false as const,
    search,
    handleSearchChange,
    clearSearch,
    page: 1,
    goToPage: (_: number) => {},
    nextPage: () => {},
    prevPage: () => {},
    updateItemInCache,
    removeItemFromCache,
  };
}
